import { API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';
import UserManagement from './UserManagement';
import RoleManagement from './RoleManagement';
import ReferenceDataManagement from './ReferenceDataManagement';
import UserRoleManagement from '../components/UserRoleManagement';
import GadgetPermissionManagement from '../components/GadgetPermissionManagement';
import ReportsSection from '../components/ReportsSection';
import ClinicLogoSection from '../components/ClinicLogoSection';
import ThemeSection from '../components/ThemeSection';

const AdminDashboard = () => {
  // Active section state
  const [activeSection, setActiveSection] = useState(null);

  // User permissions state
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // Helper function to check if user has permission for a gadget
  const hasPermission = (gadgetName, permissionType = 'VIEW') => {
    const permission = userPermissions[gadgetName];
    return permission && permission[permissionType] === true;
  };

  // Function to fetch user permissions
  const fetchUserPermissions = async () => {
    setPermissionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPermissionsLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/gadgetPermissions/my-permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const permissions = await response.json();
        setUserPermissions(permissions);
        console.log('Admin Dashboard - User permissions:', permissions);
      } else {
        console.error('Failed to fetch user permissions');
        setUserPermissions({});
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setUserPermissions({});
    } finally {
      setPermissionsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPermissions();
  }, []);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem' }}>
      <h2 style={{ marginBottom: '2rem' }}>Admin Dashboard</h2>
      
      {/* Dashboard Gadgets/Buttons */}
      {permissionsLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
          Loading dashboard permissions...
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        
        {/* User Management Gadget */}
        {hasPermission('User Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>👥 User Management</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Manage system users, roles, and permissions
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'userManagement' ? null : 'userManagement')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'userManagement' ? '#dc3545' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'userManagement' ? 'Close Management' : 'Manage Users'}
            </button>
          </div>
        )}

        {/* Role Management Gadget */}
        {hasPermission('Role Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>🔐 Role Management</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Create and manage user roles and permissions
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'roleManagement' ? null : 'roleManagement')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'roleManagement' ? '#dc3545' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'roleManagement' ? 'Close Management' : 'Manage Roles'}
            </button>
          </div>
        )}

        {/* User Role Management Gadget */}
        {hasPermission('User Role Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>👥 User Roles</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Assign multiple roles to users and manage role assignments
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'userRoles' ? null : 'userRoles')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'userRoles' ? '#dc3545' : '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'userRoles' ? 'Close Roles' : 'Manage User Roles'}
            </button>
          </div>
        )}

        {/* Gadget Permission Management Gadget */}
        {hasPermission('Gadget Permission Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>🔧 Gadget Permissions</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Control access to specific dashboard gadgets and features
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'gadgetPermissions' ? null : 'gadgetPermissions')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'gadgetPermissions' ? '#dc3545' : '#fd7e14',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'gadgetPermissions' ? 'Close Permissions' : 'Manage Permissions'}
            </button>
          </div>
        )}

        {/* Reference Data Management Gadget */}
        {hasPermission('Reference Data Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📚 Reference Data</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Manage medicines, procedures, and other reference data
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'referenceData' ? null : 'referenceData')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'referenceData' ? '#dc3545' : '#6f42c1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'referenceData' ? 'Close Reference' : 'Manage Data'}
            </button>
          </div>
        )}
        
        {hasPermission('Clinic Logo') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>Clinic Logo</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Set the prescription logo and the logo shown at the top left.
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'clinicLogo' ? null : 'clinicLogo')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'clinicLogo' ? '#dc3545' : '#1f4d40',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'clinicLogo' ? 'Close Logo' : 'Set Logo'}
            </button>
          </div>
        )}

        {hasPermission('Theme') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>Theme</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Choose the clinic theme and the prescription color.
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'theme' ? null : 'theme')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'theme' ? '#dc3545' : '#1f4d40',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'theme' ? 'Close Theme' : 'Set Theme'}
            </button>
          </div>
        )}

        {hasPermission('Reports') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📊 Reports</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Choose fields, download Excel, and schedule reports
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'reports' ? null : 'reports')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'reports' ? '#dc3545' : '#198754',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'reports' ? 'Close Reports' : 'Open Reports'}
            </button>
          </div>
        )}

        {/* No gadgets available message */}
        {!permissionsLoading && !hasPermission('User Management') && !hasPermission('Role Management') && !hasPermission('User Role Management') && !hasPermission('Gadget Permission Management') && !hasPermission('Reference Data Management') && !hasPermission('Reports') && !hasPermission('Clinic Logo') && !hasPermission('Theme') && (
          <div style={{ 
            gridColumn: '1 / -1', 
            textAlign: 'center', 
            padding: '2rem', 
            color: '#6c757d',
            backgroundColor: '#f8f9fa',
            border: '1px solid #ddd',
            borderRadius: '8px'
          }}>
            <h3>No Dashboard Access</h3>
            <p>You don't have permission to access any dashboard gadgets. Please contact your administrator.</p>
          </div>
        )}
      </div>
      )}

      {/* Conditional Sections */}
      {activeSection === 'userManagement' && hasPermission('User Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>User Management</h3>
          <UserManagement token={localStorage.getItem('token')} />
        </div>
      )}

      {activeSection === 'roleManagement' && hasPermission('Role Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Role Management</h3>
          <RoleManagement />
        </div>
      )}

      {activeSection === 'userRoles' && hasPermission('User Role Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>User Role Management</h3>
          <UserRoleManagement />
        </div>
      )}

      {activeSection === 'gadgetPermissions' && hasPermission('Gadget Permission Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Gadget Permission Management</h3>
          <GadgetPermissionManagement />
        </div>
      )}

      {activeSection === 'clinicLogo' && hasPermission('Clinic Logo') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Clinic Logo</h3>
          <ClinicLogoSection />
        </div>
      )}

      {activeSection === 'theme' && hasPermission('Theme') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Theme</h3>
          <ThemeSection />
        </div>
      )}

      {activeSection === 'reports' && hasPermission('Reports') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Reports</h3>
          <ReportsSection />
        </div>
      )}

      {activeSection === 'referenceData' && hasPermission('Reference Data Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Reference Data Management</h3>
          <ReferenceDataManagement />
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
